import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Download, FileText, FileCode, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

type ExportFormat = 'markdown' | 'html' | 'postman';

interface FormatOption {
  value: ExportFormat;
  label: string;
  description: string;
  icon: React.ReactNode;
  filename: string;
  mimeType: string;
}

const FORMAT_OPTIONS: FormatOption[] = [
  {
    value: 'markdown',
    label: 'Markdown Documentation',
    description: 'Complete spec as .md file for docs, wikis, or GitHub',
    icon: <FileText className="h-5 w-5" />,
    filename: 'adf-v2.0-specification.md',
    mimeType: 'text/markdown',
  },
  {
    value: 'html',
    label: 'PDF-Ready HTML',
    description: 'Styled HTML document - use browser print to save as PDF',
    icon: <FileCode className="h-5 w-5" />,
    filename: 'adf-v2.0-specification.html',
    mimeType: 'text/html',
  },
  {
    value: 'postman',
    label: 'Postman Collection',
    description: 'API collection with example ADF XML for testing',
    icon: <FileCode className="h-5 w-5" />,
    filename: 'ADF-v2.0-Collection.postman_collection.json',
    mimeType: 'application/json',
  },
];

interface AdfSpecDownloadDialogProps {
  trigger?: React.ReactNode;
}

export function AdfSpecDownloadDialog({ trigger }: AdfSpecDownloadDialogProps) {
  const [open, setOpen] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('markdown');
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    const format = FORMAT_OPTIONS.find((f) => f.value === selectedFormat);
    if (!format) return;

    setIsDownloading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-adf-spec?format=${selectedFormat}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to generate spec: ${response.statusText}`);
      }

      const content = await response.text();

      // For HTML format, open in new tab for printing to PDF
      if (selectedFormat === 'html') {
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(content);
          newWindow.document.close();
          toast.success('HTML opened in new tab - use Print → Save as PDF');
        } else {
          // Fallback to download if popup blocked
          downloadFile(content, format.filename, format.mimeType);
        }
      } else {
        downloadFile(content, format.filename, format.mimeType);
        toast.success(`Downloaded ${format.filename}`);
      }

      setOpen(false);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to generate specification. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Download Spec
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Download ADF v2.0 Specification</DialogTitle>
          <DialogDescription>
            Choose a format for the complete ADF specification, generated from the latest database.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={selectedFormat}
          onValueChange={(value) => setSelectedFormat(value as ExportFormat)}
          className="space-y-3 py-4"
        >
          {FORMAT_OPTIONS.map((option) => (
            <div key={option.value} className="flex items-start space-x-3">
              <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
              <Label
                htmlFor={option.value}
                className="flex flex-col cursor-pointer flex-1"
              >
                <div className="flex items-center gap-2">
                  {option.icon}
                  <span className="font-medium">{option.label}</span>
                </div>
                <span className="text-sm text-muted-foreground mt-0.5">
                  {option.description}
                </span>
              </Label>
            </div>
          ))}
        </RadioGroup>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDownload} disabled={isDownloading}>
            {isDownloading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Download
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
